/**
 * CONNOX GMBH CONFIDENTIAL
 * ________________________
 *
 * Copyright (c) 2005 - 2020 Connox GmbH, All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains the property
 * of Connox GmbH and its suppliers, if any. The intellectual and
 * technical concepts contained herein are proprietary to Connox GmbH and
 * its suppliers and may be covered by German Laws and Foreign Patents,
 * patents in process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material is
 * strictly forbidden unless prior written permission is obtained from
 * Connox GmbH.
 */

import Axios, {AxiosRequestConfig, AxiosResponse, Method} from "axios";
import {ApiError} from "../cart/requestHandlers/ApiError";
import {CartError} from "../cart/requestHandlers/CartError";
import {IRequestResponse} from "./IRequestResponse";

const axios = Axios.create({
  baseURL: __API_URL__,
  responseType: "json",
  timeout: 20000,
  withCredentials: false,
});

export class BaseRequestHandler {
  /**
   * Default request method. Returns Promise<T> or throws
   * an error when success == false or no result returned
   * @param method "GET" | "POST" | "PUT" | "PATCH" | "DELETE"
   * @param path api path
   * @param config params of type U wrapped in an object of type IBaseRequestConfiguration
   */
  public static async performRequest<T, U = undefined>(
    method: Method,
    path: string,
    config?: IBaseRequestConfiguration<U>,
  ): Promise<T> {
    let response: AxiosResponse<IRequestResponse<T>>;

    const axiosConfig: AxiosRequestConfig = {
      ...config,
      method,
      url: path,
    };

    try {
      response = await axios.request<IRequestResponse<T>>(axiosConfig);
    } catch (e) {
      throw new ApiError("An error occurred executing the request", e);
    }

    const result = response.data;
    if (!result.success) {
      if (!result.message) {
        throw new ApiError("An unknown error occurred", result);
      } else {
        throw new CartError(result.message);
      }
    }

    return result.result as T;
  }

  public static async performRequestWithPossibleError<T, U = undefined>(
    method: Method,
    path: string,
    config?: IBaseRequestConfiguration<U>,
  ): Promise<T | Error | ApiError> {
    let response: AxiosResponse<IRequestResponse<T | Error>>;

    const axiosConfig: AxiosRequestConfig = {
      ...config,
      method,
      url: path,
    };

    try {
      response = await axios.request<IRequestResponse<T>>(axiosConfig);
    } catch (e) {
      return new ApiError("An error occurred executing the request", e);
    }

    const result = response.data;
    if (!result.success) {
      if (!result.message) {
        return new ApiError("An unknown error occurred", result);
      } else {
        return new CartError(result.message);
      }
    }

    if (!result.result) {
      return new ApiError("The request result was malformed", result);
    }

    return result.result;
  }

  /**
   * same as 'performRequest' but no check on `success` or `result`
   * also capable of sending form data content type
   * @param method "GET" | "POST" | "PUT" | "PATCH" | "DELETE"
   * @param path api path
   * @param baseURL api location
   * @param config params of type U wrapped in an object of type IBaseRequestConfiguration
   */
  public static async performRequestNoResult<U = undefined>(
    method: Method,
    path: string,
    baseURL?: string,
    config?: IBaseRequestConfiguration<U>,
  ): Promise<IRequestResponse> {
    let response: AxiosResponse<IRequestResponse>;

    const axiosConfig: AxiosRequestConfig = {
      ...config,
      method,
      url: path,
    };
    if (baseURL) {
      axiosConfig.baseURL = baseURL;
    }
    if (config && config.data && config.data instanceof FormData) {
      axiosConfig.headers = {
        "Content-Type": "application/x-www-form-urlencoded",
        "accept": "text/html,application/xhtml+xml,application/xml",
      };
    }
    try {
      response = await axios.request<IRequestResponse>(axiosConfig);
    } catch (err) {
      throw new ApiError("An error occurred executing form request", err);
    }

    if (!response.data.success) {
      throw new ApiError("Request was not successful", response.data);
    }

    return response.data;
  }
}

export interface IBaseRequestConfiguration<PayloadType = undefined> {
  baseURL?: string;
  headers?: {[field: string]: string};
  data?: PayloadType;
  params?: {[field: string]: string | number | string[] | number[] | boolean | undefined};
}
